import type { User } from '@clockwise/clients/users';
import { CheckRow, Combobox } from '@/components/custom/combobox';
import {
  type EntityPickerConfig,
  userLabel,
  userPickerConfig,
} from '@/components/custom/entity-pickers';
import { PickerTruncationNotice } from '@/components/custom/picker-truncation-notice';
import {
  type LoadStrategy,
  useEntityAutocomplete,
} from '@/components/custom/use-entity-autocomplete';
import { usersApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

// Picker for a user. Backend `q` ORs ILIKE across first_name, last_name, and
// email. `excludeIds` hides users from results (e.g. the manager picker
// excludes the user being edited so a user can't become their own manager).
//
// `loadStrategy` controls how the user list is fetched — see
// `useEntityAutocomplete` for the full description. Defaults to 'lazy'
// (server-side search): a tenant can have more employees than the users
// endpoint's 200-row page cap, so 'preload' would only ever load the first
// 200 by first name and silently fail to find anyone past that. Lazy sends
// the typed query to the server, which searches the full employee list.
interface BaseProps {
  value: string | null;
  onChange: (user: User | null) => void;
  disabled?: boolean;
  placeholder?: string;
  excludeIds?: readonly string[];
  className?: string;
  hasError?: boolean;
  loadStrategy?: LoadStrategy;
  /** Page size for the one-shot 'preload' fetch (in-memory filtering). Only
   *  used when `loadStrategy="preload"`; capped at the server's 500-row limit.
   *  Defaults to the hook's 200. */
  preloadLimit?: number;
  /** Show the clear (X) affordance so a selected user can be unset back to
   *  "none" — e.g. widening a scoped picker back to "all". Off by default. */
  clearable?: boolean;
}

// serviceRoleId and srControllerCandidates scope the SAME underlying fetch
// and are mutually exclusive — a discriminated union so passing both is a
// compile error (caught for every typed call site) rather than only a
// runtime throw (which only catches it wherever that code path happens to
// render). The runtime guard in the component body stays too, as a backstop
// for untyped/JS callers and prop-spreading that bypasses the type check.
type ScopeProps =
  | {
      /** Restrict results to active employees holding this service role
       *  (e.g. the customer Sales Rep picker passes the SALES role id).
       *  Flows into the query key so a scoped picker caches separately. */
      serviceRoleId?: never;
      srControllerCandidates?: never;
    }
  | { serviceRoleId: string; srControllerCandidates?: never }
  | {
      serviceRoleId?: never;
      /**
       * Restrict results to Sr. Controller candidates — active users already
       * acting as one for somebody, plus `includeId` (the record being edited)
       * so a not-yet-established Sr. Controller can still find and pick
       * themselves. A plain employee list would offer every hire as a choice,
       * most of whom never oversee anyone. Pass `includeId: null` on a create
       * form (no id to include yet). Omit the whole prop for an unscoped picker
       * (e.g. Manager).
       */
      srControllerCandidates: { includeId: string | null };
    };

type Props = BaseProps & ScopeProps;

// List/detail fetch + key contract is shared with EntityMultiSelect via
// `entity-pickers` so the two never compute divergent query keys.
const labelOf = userLabel;
const {
  getId: getUserId,
  fetchList: fetchUserList,
  fetchOne: fetchUserOne,
  listKey: userListKey,
  detailKey: userDetailKey,
  lazyLimit: userLazyLimit,
  maxLimit: userMaxLimit,
} = userPickerConfig;
const userFilter = (u: User, q: string) => {
  const hay = `${labelOf(u)} ${u.email}`.toLowerCase();
  return hay.includes(q.toLowerCase());
};

/** Sort/pagination shape shared by every scoped user-list config below, so
 *  each one only has to state its own extra filter instead of restating
 *  the base query. */
function baseUserListQuery(opts: { q?: string; limit: number }) {
  return {
    q: opts.q,
    sortField: 'firstName' as const,
    sortDirection: 'asc' as const,
    limit: opts.limit,
    offset: 0,
  };
}

/** fetchList + listKey scoped to one service role, reusing the same
 *  active/sort contract as `userPickerConfig` so the two share semantics. */
function scopedUserConfig(
  serviceRoleId: string,
): Pick<EntityPickerConfig<User>, 'fetchList' | 'listKey'> {
  const query = (opts: { q?: string; limit: number }) => ({
    ...baseUserListQuery(opts),
    active: true,
    serviceRoleId,
  });
  return {
    fetchList: (opts) => usersApi.list(query(opts)),
    listKey: (opts) => queryKeys.users.list(query(opts)),
  };
}

/** fetchList + listKey scoped to Sr. Controller candidates (see the
 *  `srControllerCandidates` prop doc). Hits the dedicated endpoint, not
 *  `usersApi.list`, and keys into its own query-key namespace so it never
 *  shares a cache entry with the plain employee list. Deliberately does NOT
 *  send `active` — the backend embeds the active check into the "known
 *  controller" branch only (`UserRepository.srControllerCandidateFilter`),
 *  so `includeId` can still surface a terminated employee for
 *  self-assignment; a generic `active: true` here would AND across both
 *  branches and defeat that. */
function srControllerCandidatesConfig(
  includeId: string | null,
): Pick<EntityPickerConfig<User>, 'fetchList' | 'listKey'> {
  const query = (opts: { q?: string; limit: number }) => ({
    ...baseUserListQuery(opts),
    includeId: includeId ?? undefined,
  });
  return {
    fetchList: (opts) => usersApi.listSrControllerCandidates(query(opts)),
    listKey: (opts) => queryKeys.users.srControllerCandidates(query(opts)),
  };
}

export function UserAutocomplete({
  value,
  onChange,
  disabled,
  placeholder = 'Select employee…',
  excludeIds,
  className,
  hasError,
  loadStrategy = 'lazy',
  preloadLimit,
  clearable = false,
  serviceRoleId,
  srControllerCandidates,
}: Props) {
  // Mutually exclusive scopes. `Props`'s discriminated union already makes
  // passing both a compile error for typed call sites; this is a runtime
  // backstop for untyped/JS callers or prop-spreading that bypasses the
  // type check — fail loudly rather than have serviceRoleId silently win
  // and the Sr. Controller candidate restriction quietly vanish.
  if (serviceRoleId && srControllerCandidates) {
    throw new Error(
      'UserAutocomplete: serviceRoleId and srControllerCandidates are mutually exclusive scopes',
    );
  }
  // When scoped to a service role or to Sr. Controller candidates, fetch a
  // filtered list (keyed with the scoping params so it caches separately).
  // fetchOne/detailKey stay shared — a user resolves to the same row
  // regardless of the active filter.
  const scoped = serviceRoleId
    ? scopedUserConfig(serviceRoleId)
    : srControllerCandidates
      ? srControllerCandidatesConfig(srControllerCandidates.includeId)
      : { fetchList: fetchUserList, listKey: userListKey };
  const auto = useEntityAutocomplete<User>({
    value,
    strategy: loadStrategy,
    excludeCount: excludeIds?.length ?? 0,
    fetchList: scoped.fetchList,
    fetchOne: fetchUserOne,
    listKey: scoped.listKey,
    detailKey: userDetailKey,
    lazyLimit: userLazyLimit,
    maxLimit: userMaxLimit,
    preloadLimit,
  });

  const exclude = new Set(excludeIds ?? []);
  const items = auto.items.filter((u) => !exclude.has(u.id));

  return (
    <Combobox<User>
      items={items}
      selectedItem={auto.selectedItem}
      value={value}
      onChange={onChange}
      getId={getUserId}
      getLabel={labelOf}
      filterFn={auto.isClient ? userFilter : undefined}
      onQueryChange={auto.isClient ? undefined : auto.setQuery}
      onOpenChange={auto.setOpen}
      loading={auto.loading}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      hasError={hasError}
      // The employee list outgrew one page: say so rather than letting the
      // visible rows pass for every employee.
      renderFooter={
        auto.truncated
          ? () => (
              <PickerTruncationNotice
                truncated={auto.truncated}
                noun="employees"
                clientFiltered={auto.isClient}
              />
            )
          : undefined
      }
      renderItem={(u, { isSelected }) => (
        <CheckRow isSelected={isSelected} label={labelOf(u)} secondary={u.email} />
      )}
    />
  );
}
